/**
 * The processing boards' extra icons (alignment map sprite, same 24×24 stroke style as the gate
 * kit's): broom, hopper, machine, plus, mixing, scoop, small scale, stamp, tree, gauge. The gate
 * kit's sprite is not edited; these symbols are added next to it on every processing screen, so
 * kit pieces that take an icon name (Cta, Btn, Strip, Band) can use them too.
 */
import type { CSSProperties } from 'react'
import type { V1IconName } from '@/features/gate/v1/sprite'

export type ProcessIconName =
  | 'broom'
  | 'hopper'
  | 'machine'
  | 'plus'
  | 'mixing'
  | 'scoop'
  | 'smallscale'
  | 'stamp'
  | 'tree'
  | 'gauge'

export type PIconName = V1IconName | ProcessIconName

const PROCESS_SPRITE =
  '<symbol id="ui-broom" viewBox="0 0 24 24"><path d="M14.5 2.5L10.5 11"/><path d="M6 11h9l2 9H4z"/><path d="M8 15v5M12 15v5"/></symbol>' +
  '<symbol id="ui-hopper" viewBox="0 0 24 24"><path d="M4 4h16l-6 8v6h-4v-6z"/></symbol>' +
  '<symbol id="ui-machine" viewBox="0 0 24 24"><rect x="4" y="9" width="16" height="9" rx="1.5"/><path d="M8 9l2-5h4l2 5"/><circle cx="12" cy="13.5" r="2.5"/><path d="M6 18v2M18 18v2"/></symbol>' +
  '<symbol id="ui-plus" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></symbol>' +
  '<symbol id="ui-mixing" viewBox="0 0 24 24"><path d="M4 7h4l8 10h4M4 17h4l3-4M14 10l2-3h4"/><path d="M18 5l2 2-2 2M18 15l2 2-2 2"/></symbol>' +
  '<symbol id="ui-scoop" viewBox="0 0 24 24"><path d="M3 14c0-3 3-5 7-5h4l1-2h3l3 3-3 3h-3l-1-1h-1c0 4-3 7-6 7s-4-2-4-5z"/></symbol>' +
  '<symbol id="ui-smallscale" viewBox="0 0 24 24"><rect x="4" y="9" width="16" height="11" rx="2"/><rect x="7.5" y="12" width="9" height="4" rx="1"/><path d="M6 9V6h12v3"/></symbol>' +
  '<symbol id="ui-stamp" viewBox="0 0 24 24"><path d="M9 4h6v5l2 3H7l2-3z"/><path d="M5 12h14v4H5zM5 20h14"/></symbol>' +
  '<symbol id="ui-tree" viewBox="0 0 24 24"><circle cx="12" cy="5" r="2"/><circle cx="6" cy="19" r="2"/><circle cx="18" cy="19" r="2"/><path d="M12 7v5M12 12H6v5M12 12h6v5"/></symbol>' +
  '<symbol id="ui-gauge" viewBox="0 0 24 24"><path d="M4 18a8 8 0 1 1 16 0"/><path d="M12 18l4-6"/></symbol>'

/** The extra symbols, once per processing screen (absolutely placed, so it takes no room). */
export function ProcessSprite() {
  return (
    <div
      aria-hidden="true"
      style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
      dangerouslySetInnerHTML={{ __html: `<svg>${PROCESS_SPRITE}</svg>` }}
    />
  )
}

/** A gate-kit or processing icon name where a kit piece types it as the gate's names. */
export const k = (name: PIconName) => name as V1IconName

export function PIcon({
  name,
  size = 24,
  sw = 2,
  color,
  style,
}: {
  name: PIconName
  size?: number
  sw?: number
  /** Stroke colour; currentColor by default. */
  color?: string
  style?: CSSProperties
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? 'currentColor'}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0, ...style }}
    >
      <use href={`#ui-${name}`} />
    </svg>
  )
}

/** The step pictures of the machine pick list (dehull, grade, clean), drawn as on the board. */
export function StepIcon({ step, size = 26 }: { step: string; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  if (step === 'DEHULL' || step === 'SPLIT')
    return (
      <svg {...common}>
        <path d="M3.5 15c0-5 3.5-9 8-9.5" />
        <path d="M20.5 9c0 5-3.5 9-8 9.5" />
        <ellipse cx="12" cy="12" rx="2.6" ry="3.4" />
      </svg>
    )
  if (step === 'GRADE' || step === 'SORT')
    return (
      <svg {...common}>
        <circle cx="6" cy="14.5" r="4" />
        <circle cx="14" cy="15.5" r="3" />
        <circle cx="20" cy="16.5" r="2" />
        <path d="M2 20.5h20" />
      </svg>
    )
  if (step === 'CLEAN' || step === 'POLISH' || step === 'DRY')
    return (
      <svg {...common}>
        <path d="M3 8h18" />
        <path d="M4 8c1 5 4 7.5 8 7.5s7-2.5 8-7.5" />
        <path d="M9 19v.01M12 21v.01M15 19v.01" />
        <circle cx="16.5" cy="4.5" r="1.6" />
      </svg>
    )
  return <PIcon name="machine" size={size} />
}

/** The crop picture (groundnut, moong), else a sack. */
export function cropIcon(cropId: string | null | undefined): V1IconName {
  const c = (cropId ?? '').toLowerCase()
  if (c.includes('groundnut')) return 'groundnut'
  if (c.includes('moong')) return 'moong'
  return 'sack'
}
